import { Suspense, lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { escalationAlertOf } from '@/lib/airline-reassignment/escalation';
import { formatFlightClock24 } from '@/lib/disruptions/airport-tz';
import type { DisruptionDetail } from '@/lib/disruptions/types';
import { useDisruptionDelays } from '@/lib/delay/use-delay';
import { useGraceClock } from './use-grace-clock';

// The disruption header's escalation alert (flight-delay U5, M2). A delayed flight that
// ends CANCELLED gives the airline a grace window to reassign its own passengers before
// Nexa's rebooking takes the rest (`flight-delay.tds.md` §15):
//
//   grace    — "Flight cancelled after delay — the airline has until HH:MM to reassign"
//              with a live countdown to `graceUntil` (`use-now.ts`: a render tick, never
//              a poll); past `graceUntil` and before the release lands, it says so;
//   released — the summary: reassigned by the airline N · sent to Nexa rebooking N ·
//              conflicts N;
//   "Details" opens the per-flight, per-group view (lazy — nothing of it loads until opened).
//
// Data: the disruption's delay timelines (the SAME query and cache entry as U1's header
// badge, so one read), kept live by the WS merge (`FlightEscalated` / `FlightDelayChanged`
// → `applyEvent`). Before they answer, the row's `flightStatus@v1.graceUntil` already
// drives the countdown.
//
// UX states: EMPTY — nothing escalated ⇒ renders nothing; LOADING — the row says the
// flight escalated but only the timelines can say more ⇒ a skeleton line; ERROR — that
// read failed ⇒ one line + Retry; SUCCESS — the alert. Phone: everything wraps inside the
// header, the button is a 44 px target below `sm`. Theming: tokens only.

const EscalationDetailsDialog = lazy(() =>
  import('./escalation-details-dialog').then((m) => ({ default: m.EscalationDetailsDialog })),
);

export function EscalationAlert({ detail }: { detail: DisruptionDetail }) {
  const { t } = useTranslation('disruptions');
  const [open, setOpen] = useState(false);
  const status = detail.flightStatus;
  const enabled = status?.escalated === true || typeof status?.maxDelayMinutes === 'number';
  const delays = useDisruptionDelays(detail.urn, enabled);
  const model = escalationAlertOf(status, delays.data);

  if (!model) return null;

  if (model.mode === 'pending') {
    if (delays.isError) {
      return (
        <div
          className="mt-2 flex max-w-full flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-xs"
          role="alert"
          data-testid="escalation-alert-error"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />
          <span className="min-w-0 break-words">{t('escalation.alert.error')}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-11 px-3 text-xs sm:h-7 sm:px-2"
            onClick={() => void delays.refetch()}
            data-testid="escalation-alert-retry"
          >
            {t('escalation.alert.retry')}
          </Button>
        </div>
      );
    }
    return (
      <div className="mt-2 max-w-full" data-testid="escalation-alert-loading" aria-busy="true">
        <span className="sr-only">{t('escalation.alert.loading')}</span>
        <Skeleton className="h-7 w-full max-w-md" />
      </div>
    );
  }

  const origin = model.originIata ?? detail.cases?.find((c) => c.origin)?.origin;
  return (
    <div
      className="mt-2 flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-1.5 text-xs"
      data-testid="escalation-alert"
      data-mode={model.mode}
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />
      {model.mode === 'grace' ? (
        <GraceLine graceUntil={model.graceUntil} origin={origin} />
      ) : (
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5" role="status" data-testid="escalation-alert-summary">
          <span className="font-semibold">{t('escalation.alert.released')}</span>
          <span data-testid="escalation-alert-reassigned">{t('escalation.alert.summary.reassigned', { count: model.reassigned })}</span>
          <span aria-hidden>·</span>
          <span data-testid="escalation-alert-released">{t('escalation.alert.summary.released', { count: model.released })}</span>
          <span aria-hidden>·</span>
          <span data-testid="escalation-alert-conflicts">{t('escalation.alert.summary.conflicts', { count: model.conflicts })}</span>
        </span>
      )}
      {model.mode === 'grace' && model.conflicts > 0 ? (
        <span className="font-medium text-destructive" data-testid="escalation-alert-grace-conflicts">
          {t('escalation.alert.conflicts', { count: model.conflicts })}
        </span>
      ) : null}
      {model.flights > 1 ? (
        <span className="text-muted-foreground" data-testid="escalation-alert-more">
          {t('escalation.alert.moreFlights', { count: model.flights - 1 })}
        </span>
      ) : null}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-11 px-3 text-xs sm:h-7 sm:px-2"
            aria-label={t('escalation.alert.detailsAria')}
            data-testid="escalation-alert-details"
          >
            {t('escalation.alert.details')}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[90dvh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('escalation.dialog.title')}</DialogTitle>
            <DialogDescription>{t('escalation.dialog.description')}</DialogDescription>
          </DialogHeader>
          {open ? (
            <Suspense fallback={<Skeleton className="h-40 w-full" />}>
              <EscalationDetailsDialog disruptionUrn={detail.urn} cases={detail.cases ?? []} />
            </Suspense>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The grace sentence + the live countdown — the only part that re-renders each second.
 * The sentence is a polite live region (said once); the ticking countdown sits outside
 * it, so a screen reader is not read a number every second.
 */
function GraceLine({ graceUntil, origin }: { graceUntil: string; origin: string | undefined }) {
  const { t } = useTranslation('disruptions');
  const clock = useGraceClock(graceUntil);
  if (clock.lapsed) {
    return (
      <span className="min-w-0 break-words font-semibold" role="status" data-testid="escalation-alert-lapsed">
        {t('escalation.alert.lapsed')}
      </span>
    );
  }
  return (
    <>
      <span className="min-w-0 break-words font-semibold" role="status" data-testid="escalation-alert-grace">
        {t('escalation.alert.grace', { time: formatFlightClock24(graceUntil, origin) })}
      </span>
      {clock.hasClock ? (
        <span className="shrink-0 tabular-nums text-destructive" data-testid="escalation-alert-countdown">
          {t('escalation.alert.remaining', { remaining: clock.remaining })}
        </span>
      ) : null}
    </>
  );
}
